import { inject, injectable } from "tsyringe";
import { Observable, of } from "rxjs";
import { catchError, map, mergeMap } from "rxjs/operators";
import { ProductCategories } from "src/constants/product-categories";
import { PolicyBridgeService } from "src/containers/insurances/services/policy-bridge/policy-bridge-service";
import { DayjsUtils } from "src/libs/dayjs-utils";
import { autoBind } from "src/shared/decorators/autobind";
import { PgaPolicy } from "src/shared/models/pga-policy.model";
import { VehiclePolicy } from "src/shared/models/vehicle-policy.model";
import type {
    GetAssistancesType,
    SendAssistanceFormType,
} from "src/containers/vehicular-assistance/core/repositories/vehicular-assistance/vehicular-assistance-repository";
import { VehicularAssistanceRepository } from "src/containers/vehicular-assistance/core/repositories/vehicular-assistance/vehicular-assistance-repository";
import { VehicularAssistance } from "src/containers/vehicular-assistance/core/models/assistance";
import { ArrayUtils } from "src/libs/array";
import type {
    Coordinates,
    VehicularAssistanceMemory,
} from "src/containers/vehicular-assistance/constants/types";
import { Log } from "src/libs/log";

@injectable()
export class VehicularAssistanceService {
    private readonly STORAGE_KEY = "VEHICULAR_ASSISTANCE_MEMORY";
    private readonly STORAGE_KEY_TYPE = "VEHICULAR_ASSISTANCE_MEMORY_TYPE";
    private readonly ALLOWED_CATEGORIES = [ProductCategories.AUTOS];

    constructor(
        @inject("VehicularAssistanceRepository")
        private repository: VehicularAssistanceRepository,
        @inject("PolicyBridgeService") private bridge: PolicyBridgeService
    ) {}

    @autoBind
    getAssistances(
        requestData: GetAssistancesType
    ): Observable<Array<VehicularAssistance>> {
        return this.repository.getAssistances(requestData, false).pipe(
            map((data) => {
                const correctedAssistances = data.map((service) => {
                    const newSubServices = service.subServices.map(
                        (subservice) => ({
                            ...subservice,
                            code: `${service.code}-${subservice.code}`,
                        })
                    );
                    return { ...service, subServices: newSubServices };
                });
                const assistances = ArrayUtils.flatByKey<Array<object>>(
                    correctedAssistances,
                    "subServices"
                );
                return assistances.map((x) =>
                    VehicularAssistance.fromJson<VehicularAssistance>(x)
                );
            })
        );
    }

    @autoBind
    sendAssistanceForm(formData: SendAssistanceFormType): Observable<boolean> {
        return this.repository.sendAssistanceForm(formData).pipe(
            mergeMap(({ requestId }) => {
                return this.repository.requestCaseStatus(requestId).pipe(
                    mergeMap((rawResponse) => {
                        const response = JSON.parse(rawResponse) as {
                            action: string;
                        };

                        const ACTION_ERROR = "ERROR";
                        const isActionError = response.action !== ACTION_ERROR;
                        return of(isActionError);
                    })
                );
            }),
            catchError((err) => {
                Log.error("err", err);
                return of(false);
            })
        );
    }

    getCoverageCoordinates(): Observable<Array<Coordinates>> {
        return this.repository.getCoverageCoordinates().pipe(
            map((data) => {
                const [firstGroup] = data.features;
                const { coordinates } = firstGroup.geometry;
                return coordinates[0].map(([longitude, latitude]) => ({
                    latitude,
                    longitude,
                }));
            })
        );
    }

    @autoBind
    hasAccess(policies: Array<PgaPolicy>) {
        const [category] = this.ALLOWED_CATEGORIES;
        const vehiclePolicies =
            this.bridge.getPoliciesByCategory<VehiclePolicy>(
                policies,
                category
            );
        const validPolicies = this.getPoliciesInForce(vehiclePolicies);
        return validPolicies.length > 0;
    }

    @autoBind
    private getPoliciesInForce(policies: Array<PgaPolicy>) {
        return policies.filter((policy) => {
            const now = DayjsUtils.today();
            const expDate = DayjsUtils.getDate(new Date(policy.validityEnd));
            return DayjsUtils.isBefore(expDate, now);
        });
    }

    @autoBind
    storeInProgressData(data: Partial<VehicularAssistanceMemory>) {
        const currentData = this.getInProgressData();
        const finalData = {
            ...currentData,
            ...data,
        };
        Log.info("Storing data:", finalData);
        if (finalData.assistanceType) {
            localStorage.setItem(
                this.STORAGE_KEY_TYPE,
                finalData.assistanceType
            );
        }
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(finalData));
    }

    getInProgressData(): VehicularAssistanceMemory {
        const data = localStorage.getItem(this.STORAGE_KEY);
        if (data) {
            return JSON.parse(data) as VehicularAssistanceMemory;
        }
        return {} as VehicularAssistanceMemory;
    }

    clearInProgressData(): void {
        localStorage.removeItem(this.STORAGE_KEY);
    }

    getInProgressType(): string {
        return localStorage.getItem(this.STORAGE_KEY_TYPE) || "";
    }
}
